'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

export type Theme = 'dark' | 'light' | 'system';
export type ResolvedTheme = 'dark' | 'light';

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  chartColors: ChartColorSet;
}

export interface ChartColorSet {
  primary: string;
  success: string;
  error: string;
  warning: string;
  accent: string;
  grid: string;
  axis: string;
  tooltip: string;
  tooltipBorder: string;
  areaGradientStart: string;
  areaGradientEnd: string;
  benchmarkColor: string;
  drawdownColor: string;
  background: string;
  surface: string;
}

const DARK_CHART_COLORS: ChartColorSet = {
  primary: '#0EA5E9',
  success: '#10B981',
  error: '#EF4444',
  warning: '#F59E0B',
  accent: '#F59E0B',
  grid: '#334155',
  axis: '#94A3B8',
  tooltip: '#1E293B',
  tooltipBorder: '#334155',
  areaGradientStart: 'rgba(14,165,233,0.3)',
  areaGradientEnd: 'rgba(14,165,233,0)',
  benchmarkColor: '#8B5CF6',
  drawdownColor: '#EF4444',
  background: '#0F172A',
  surface: '#1E293B',
};

const LIGHT_CHART_COLORS: ChartColorSet = {
  primary: '#0284C7',
  success: '#059669',
  error: '#DC2626',
  warning: '#D97706',
  accent: '#D97706',
  grid: '#E2E8F0',
  axis: '#64748B',
  tooltip: '#FFFFFF',
  tooltipBorder: '#CBD5E1',
  areaGradientStart: 'rgba(2,132,199,0.2)',
  areaGradientEnd: 'rgba(2,132,199,0)',
  benchmarkColor: '#7C3AED',
  drawdownColor: '#DC2626',
  background: '#F8FAFC',
  surface: '#FFFFFF',
};

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  resolvedTheme: 'dark',
  setTheme: () => {},
  toggleTheme: () => {},
  chartColors: DARK_CHART_COLORS,
});

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

const LIGHT_VARS: Record<string, string> = {
  '--color-background': '#F8FAFC',
  '--color-foreground': '#0F172A',
  '--color-border': '#CBD5E1',
  '--color-input': '#F1F5F9',
  '--color-ring': '#0284C7',
  '--color-card': '#FFFFFF',
  '--color-card-foreground': '#1E293B',
  '--color-popover': '#FFFFFF',
  '--color-popover-foreground': '#1E293B',
  '--color-muted': '#E2E8F0',
  '--color-muted-foreground': '#64748B',
  '--color-primary': '#0284C7',
  '--color-primary-foreground': '#FFFFFF',
  '--color-secondary': '#94A3B8',
  '--color-secondary-foreground': '#FFFFFF',
  '--color-destructive': '#DC2626',
  '--color-destructive-foreground': '#FFFFFF',
  '--color-accent': '#D97706',
  '--color-accent-foreground': '#FFFFFF',
  '--color-success': '#059669',
  '--color-success-foreground': '#FFFFFF',
  '--color-warning': '#D97706',
  '--color-warning-foreground': '#FFFFFF',
  '--color-error': '#DC2626',
  '--color-error-foreground': '#FFFFFF',
  '--color-surface': '#F1F5F9',
  '--color-surface-foreground': '#1E293B',
  '--color-surface-elevated': '#E2E8F0',
};

const DARK_VARS: Record<string, string> = {
  '--color-background': '#0F172A',
  '--color-foreground': '#F8FAFC',
  '--color-border': '#334155',
  '--color-input': '#1E293B',
  '--color-ring': '#0EA5E9',
  '--color-card': '#1E293B',
  '--color-card-foreground': '#E2E8F0',
  '--color-popover': '#1E293B',
  '--color-popover-foreground': '#E2E8F0',
  '--color-muted': '#334155',
  '--color-muted-foreground': '#94A3B8',
  '--color-primary': '#0EA5E9',
  '--color-primary-foreground': '#FFFFFF',
  '--color-secondary': '#64748B',
  '--color-secondary-foreground': '#FFFFFF',
  '--color-destructive': '#EF4444',
  '--color-destructive-foreground': '#FFFFFF',
  '--color-accent': '#F59E0B',
  '--color-accent-foreground': '#000000',
  '--color-success': '#10B981',
  '--color-success-foreground': '#FFFFFF',
  '--color-warning': '#F59E0B',
  '--color-warning-foreground': '#000000',
  '--color-error': '#EF4444',
  '--color-error-foreground': '#FFFFFF',
  '--color-surface': '#1E293B',
  '--color-surface-foreground': '#E2E8F0',
  '--color-surface-elevated': '#334155',
};

function applyThemeVars(resolved: ResolvedTheme) {
  const vars = resolved === 'light' ? LIGHT_VARS : DARK_VARS;
  const root = document.documentElement;
  Object.entries(vars).forEach(([key, val]) => root.style.setProperty(key, val));
  root.setAttribute('data-theme', resolved);
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === 'system') return getSystemTheme();
  return theme;
}

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setThemeState] = useState<Theme>('dark');
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('dark');

  // Initialize from localStorage on mount
  useEffect(() => {
    const stored = (localStorage.getItem('apexfi-theme') as Theme) || 'dark';
    const resolved = resolveTheme(stored);
    setThemeState(stored);
    setResolvedTheme(resolved);
    applyThemeVars(resolved);
  }, []);

  // Listen for system preference changes
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => {
      const resolved = getSystemTheme();
      setResolvedTheme(resolved);
      applyThemeVars(resolved);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  const setTheme = useCallback(async (newTheme: Theme) => {
    const resolved = resolveTheme(newTheme);
    setThemeState(newTheme);
    setResolvedTheme(resolved);
    localStorage.setItem('apexfi-theme', newTheme);
    applyThemeVars(resolved);

    // Persist to Supabase if user is logged in
    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('user_profiles')
          .update({ theme_preference: newTheme, updated_at: new Date().toISOString() })
          .eq('id', user.id);
      }
    } catch {
      // Silently fail — localStorage is the source of truth
    }
  }, []);

  const toggleTheme = useCallback(() => {
    const next = resolvedTheme === 'dark' ? 'light' : 'dark';
    setTheme(next);
  }, [resolvedTheme, setTheme]);

  const chartColors = resolvedTheme === 'light' ? LIGHT_CHART_COLORS : DARK_CHART_COLORS;

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggleTheme, chartColors }}>
      {children}
    </ThemeContext.Provider>
  );
};
